import { HttpErrorResponse } from '@angular/common/http';

/**
 * Mensaje accionable y consistente para 403 (rol sin permisos sobre el módulo
 * Chatflows). Se usa en toda la UI de chatflows para no dejar spinners colgados
 * ni mostrar errores técnicos cuando el backend rechaza por permisos.
 */
export const CHATFLOW_PERMISSION_DENIED_MESSAGE =
  'No tienes permiso para esta acción. Pide a un administrador que te conceda el módulo Chatflows en Roles y permisos.';

/**
 * Extrae un mensaje legible (en español, apto para toast) de un error de API.
 *
 * Soporta el formato de error de validación de chatflows del backend:
 * `{ status, message, details: [{ code, nodeKey, nodeName, field, message }] }`
 * combinando el mensaje principal con el detalle estructurado por nodo.
 *
 * Reglas de UI (INC_CRM_019 inc 4/5/6/8):
 * - Nunca se renderiza el objeto detalle directo (evita "[object Object]").
 * - No se agrega el sufijo técnico "(400 OK)" / "(status statusText)" al mensaje
 *   visible; el código HTTP solo se registra en consola.
 * - 403 → mensaje accionable de permisos.
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    // Traza técnica solo para consola — nunca se muestra al usuario final.
    if (error.status) {
      console.warn(`[chatflows] Error HTTP ${error.status} ${error.statusText || ''}`.trim(), error.error);
    }

    // 403: rol sin permisos. Mensaje accionable y consistente en toda la UI.
    if (error.status === 403) {
      return CHATFLOW_PERMISSION_DENIED_MESSAGE;
    }

    const body = error.error;

    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }

    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;

      let main = '';
      for (const key of ['message', 'error', 'detail', 'title']) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) {
          main = value.trim();
          break;
        }
      }

      const details = record['details'];
      if (typeof details === 'string' && details.trim() && !main) {
        main = details.trim();
      }

      const detailLines = formatStructuredDetails(details)
        .filter((line) => !main.includes(line));

      if (main || detailLines.length) {
        return [main, ...detailLines].filter(Boolean).join(' • ');
      }
    }

    // Sin cuerpo útil: preferimos el fallback en español antes que el mensaje
    // técnico de Angular ("Http failure response for ...: 400 OK").
    return fallback;
  }

  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }

  if (error && typeof error === 'object') {
    const value = (error as Record<string, unknown>)['message'];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return fallback;
}

/**
 * Convierte `details` (o `errors`) en líneas legibles cuando es un array de
 * `{ code, nodeKey, nodeName, field, message }`. Solo se muestran `nodeName`
 * (nombre visible del paso) y `message`: ni la `nodeKey` ni el `code`
 * (`CAPTURE_FIELD_TYPE_INVALID`) son texto para el usuario final. Nunca
 * devuelve el objeto.
 */
function formatStructuredDetails(details: unknown): string[] {
  if (!Array.isArray(details)) {
    return [];
  }

  return details
    .map((item) => {
      if (typeof item === 'string') {
        return item.trim();
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const message = typeof record['message'] === 'string' ? record['message'].trim() : '';
        const nodeName = typeof record['nodeName'] === 'string' ? record['nodeName'].trim() : '';
        const label = nodeName;
        if (message) {
          return label ? `[${label}] ${message}` : message;
        }
        // Sin mensaje solo queda el `code` técnico (`CAPTURE_FIELD_TYPE_INVALID`) y la
        // `nodeKey`: no se muestran. La línea se descarta y queda el mensaje principal
        // o el texto por defecto en español.
        return '';
      }
      return '';
    })
    .filter((line) => !!line);
}
